import { SaveQuantifyOrCompareForm } from '@modules/analysis/components/SaveQuantifyOrCompareForm';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { NOTIFICATION_TIMEOUT_LONG_6S } from '@modules/common/constants';
import {
  useGetPromptPosition,
  useInitializeQuantifyStoreFromEqlToken,
} from '@modules/oracle/hooks';
import { OracleSearchActionType } from '@modules/oracle/store/oracleActionType';
import { useOracleSearchStore } from '@modules/oracle/store/oracleSearchStore';
import { trackWisdomEvents } from '@modules/oracle/utils/trackWisdomEvents';
import { QuantifyChart } from '@modules/quantify/components/QuantifyChart';
import {
  useFetchQuantifyReport,
  useGetFormattedDurationFieldLabels,
} from '@modules/quantify/hooks';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { InsightsRounded } from '@mui/icons-material';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import {
  AnalysisViewFragment,
  AssistantResponseEqlToken,
} from 'generated/graphql';
import { cloneDeep } from 'lodash';
import pluralize from 'pluralize';
import { useEffect, useState } from 'react';
import { WisdomQuantifyChartLink } from './WisdomQuantifyChartLink';

interface OracleQuantifyChartProps {
  tokenV2ID: string;
  promptID: string;
  assistantResponseEqlToken: AssistantResponseEqlToken;
}

export function OracleQuantifyChart({
  tokenV2ID,
  promptID,
  assistantResponseEqlToken,
}: OracleQuantifyChartProps) {
  const [shouldExecuteQuery, setShouldExecuteQuery] = useState(false);
  const [shouldOpenSaveQuantifyDialog, setShouldOpenSaveQuantifyDialog] =
    useState(false);

  const { showSuccessNotification } = useNotificationContext();
  const fetchQuantifyReport = useFetchQuantifyReport({
    isInitialFetch: true,
  });
  const { isLoading } = useInitializeQuantifyStoreFromEqlToken({
    eqlQuery: assistantResponseEqlToken.eql,
  });
  const { durationLabel } = useGetFormattedDurationFieldLabels();
  const dispatch = useOracleSearchStore(({ dispatch }) => dispatch);
  const savedEqlSet = useOracleSearchStore(
    ({ visual }) => visual.savedEqlTokenSet,
  );
  const sessionID = useOracleSearchStore(({ data }) => data.sessionID);
  const promptText = useOracleSearchStore(
    ({ data }) => data.promptByID?.[promptID].question,
  );
  const threadPosition = useGetPromptPosition(promptID);

  const addQuantifyChart = () => {
    const updatedSavedEqlSet = cloneDeep(savedEqlSet);
    updatedSavedEqlSet.add(tokenV2ID);

    dispatch({
      type: OracleSearchActionType.SET_VISUAL,
      payload: {
        savedEqlTokenSet: updatedSavedEqlSet,
      },
    });
  };

  const onQuantifySave = (
    analysis?: Pick<AnalysisViewFragment, 'title' | 'viewToken' | 'useCase'>,
  ) => {
    addQuantifyChart();
    trackWisdomEvents({
      eventName: 'Wisdom - Save as Quantify',
      sessionID,
      responseID: promptID,
      searchText: promptText,
      threadPosition,
    });

    if (analysis) {
      showSuccessNotification({
        contentComponent: `"${analysis.title}" saved successfully`,
        props: {
          timeout: NOTIFICATION_TIMEOUT_LONG_6S,
          action: (
            <WisdomQuantifyChartLink
              quantifyName={analysis.title}
              viewToken={analysis.viewToken}
            />
          ),
        },
      });
    }
  };

  useEffect(() => {
    if (!isLoading) {
      setShouldExecuteQuery(true);
    }
  }, [isLoading]);

  useEffect(() => {
    if (!shouldExecuteQuery) {
      return;
    }

    setShouldExecuteQuery(false);
    fetchQuantifyReport();
  }, [shouldExecuteQuery, fetchQuantifyReport]);

  const groupBy = useQuantifyStore(({ query }) => query.groupByField);

  const groupByLabel = !!groupBy?.detail?.metadata
    ? groupBy.displayName
    : pluralize(groupBy?.displayName || 'record');

  const isQuantifySaved = savedEqlSet.has(tokenV2ID);

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body1">
          Showing {groupByLabel} for {durationLabel.primary}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip
            title={isQuantifySaved ? 'This Quantify is saved' : undefined}
            placement="top"
            arrow
          >
            <Box>
              <Button
                size="small"
                variant="text"
                startIcon={<InsightsRounded />}
                disabled={isQuantifySaved}
                onClick={() => setShouldOpenSaveQuantifyDialog(true)}
              >
                Save as quantify
              </Button>
            </Box>
          </Tooltip>
          <SaveQuantifyOrCompareForm
            shouldSkipRedirect={true}
            isOpen={shouldOpenSaveQuantifyDialog}
            onClose={() => setShouldOpenSaveQuantifyDialog(false)}
            onCreated={onQuantifySave}
          />
        </Stack>
      </Stack>
      <QuantifyChart
        shouldShowToggleActions={false}
        shouldShowTitle={false}
        component={{
          trendChart: {
            containerSxProps: { height: 350 },
            shouldAutoAdjustHeight: true,
            shouldShowTimeRangeControls: true,
          },
          barChart: {
            sxProps: { p: 0 },
          },
          compareBarChart: {
            chartHeight: 350,
          },
        }}
        shouldShowTableView={false}
        shouldDisableAddToFilter={true}
      />
    </Stack>
  );
}
